import { useCallback, useEffect, useState } from 'react';

import { ApplicationProps } from '@/models/Application';
import { PartnerProps } from '@/models/Partner';

type UseApplicationApprovalFormProps = {
  currentApplication: ApplicationProps;
  currentPartner?: PartnerProps | null;
};

type UseApplicationApprovalFormResult = {
  specialInstructions: string;
  setSpecialInstructions: (v: string) => void;
  markSpecialDirty: () => void;

  additionalStickers: number | null | undefined;
  onAdditionalStickersChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export function useApprovalForm({
  currentApplication,
  currentPartner,
}: UseApplicationApprovalFormProps): UseApplicationApprovalFormResult {
  const partnerSpecialInstructions = currentPartner?.specialInstructions;
  const partnerAdditionalStickers = currentPartner?.additionalStickers;

  const [specialInstructions, setSpecialInstructions] = useState<string>(
    () => partnerSpecialInstructions ?? currentApplication.specialInstructions ?? ''
  );
  const [specialDirty, setSpecialDirty] = useState(false);

  const [additionalStickers, setAdditionalStickers] = useState<number | null | undefined>(
    () => partnerAdditionalStickers ?? currentApplication.additionalStickers ?? undefined
  );

  // keep in sync with context until user edits
  useEffect(() => {
    if (!specialDirty) {
      setSpecialInstructions(partnerSpecialInstructions ?? currentApplication.specialInstructions ?? '');
    }
  }, [currentApplication.specialInstructions, partnerSpecialInstructions, specialDirty]);

  // initialize stickers once (or when app changes)
  useEffect(() => {
    // if your app can change while dialog open, you probably want this to sync too
    setAdditionalStickers(partnerAdditionalStickers ?? currentApplication.additionalStickers ?? null);
  }, [currentApplication.additionalStickers, partnerAdditionalStickers]);

  const markSpecialDirty = useCallback(() => setSpecialDirty(true), []);

  const onAdditionalStickersChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    setAdditionalStickers(digits === '' ? null : Number(digits));
  }, []);

  return {
    specialInstructions,
    setSpecialInstructions,
    markSpecialDirty,
    additionalStickers,
    onAdditionalStickersChange,
  };
}
